import { useEffect, useId, useState } from 'react';
import googleDriveLogo from '../assets/integrations/google-drive-logo.png';
import { connectorLogoApi } from './connector-logo-api';
import type { ClientRecord } from './client-directory-api';
import { PrototypeIcon } from './PrototypeIcon';

/** Complément de la fiche existante. Les modules métier n'ont pas encore de parcours réel :
 * aucune valeur n'est inférée de leur absence et aucune action ne fabrique de ressource.
 * La désactivation visuelle n'est pas une autorisation ; les futurs raccords devront conserver
 * le Client explicite et leurs contrôles serveur. Les contacts existants restent indépendants.
 */
export function ClientActivityOverview({
  client,
}: {
  readonly client: Pick<ClientRecord, 'id' | 'displayName' | 'stage' | 'origin'>;
}): React.JSX.Element | null {
  const availabilityId = useId();
  const [driveLogoUrl, setDriveLogoUrl] = useState(googleDriveLogo);
  const operational = client.stage === 'CLIENT' && client.origin !== 'FREEBE_LEGACY_UNREVIEWED';
  useEffect(() => {
    if (!operational) return;
    let active = true;
    void connectorLogoApi
      .list()
      .then((logos) => {
        if (active) setDriveLogoUrl(connectorLogoApi.url('google-drive', logos) ?? googleDriveLogo);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [operational]);
  if (client.stage !== 'CLIENT' || client.origin === 'FREEBE_LEGACY_UNREVIEWED') return null;
  return (
    <section className="client-activity" aria-label={`Activité de ${client.displayName}`}>
      <div className="client-activity__actions" aria-describedby={availabilityId}>
        <button type="button" className="client-activity__project" disabled>
          <PrototypeIcon name="folder-kanban" /> Nouveau projet
        </button>
        <button type="button" className="client-activity__sav" disabled>
          <PrototypeIcon name="life-buoy" /> Nouveau SAV
        </button>
        <button type="button" className="client-activity__faq" disabled>
          <PrototypeIcon name="book-open" /> Nouvelle FAQ
        </button>
      </div>
      <p id={availabilityId} className="client-activity__availability">
        Les projets, le SAV et la FAQ ne sont pas encore disponibles dans Tosho.
      </p>
      <dl className="client-activity__metrics" aria-label="Indicateurs du Client">
        {(['Projets', 'Tickets SAV', 'Tutoriels FAQ', 'CA cumulé'] as const).map((label) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>
              <span aria-hidden="true">—</span>
              <span>Indisponible</span>
            </dd>
          </div>
        ))}
      </dl>
      <div className="client-activity__main">
        <section className="client-directory__table-card client-activity__projects">
          <header>
            <div>
              <p className="client-directory__eyebrow">Liés au Client</p>
              <h2>Projets</h2>
            </div>
            <button type="button" disabled aria-describedby={availabilityId}>
              Voir les projets
            </button>
          </header>
          <p>Le suivi des projets de ce Client sera disponible avec le module Projets.</p>
        </section>
        <section className="client-directory__table-card">
          <header>
            <div>
              <p className="client-directory__eyebrow">Synthèse</p>
              <h2>Relation client</h2>
            </div>
          </header>
          <dl className="client-activity__relation">
            <div>
              <dt>Dernier devis</dt>
              <dd>Consultation indisponible ici</dd>
            </div>
            <div>
              <dt>Factures ouvertes</dt>
              <dd>Montant et nombre indisponibles</dd>
            </div>
            <div className="client-activity__drive">
              <dt>
                <img
                  src={driveLogoUrl}
                  alt="Dossier Google Drive"
                  onError={() => setDriveLogoUrl(googleDriveLogo)}
                />
              </dt>
              <dd>
                <button type="button" className="client-activity__drive-open" disabled>
                  <PrototypeIcon name="folder-kanban" /> Ouvrir le dossier{' '}
                  <PrototypeIcon name="arrow-right" />
                </button>
                <span>Accès indisponible depuis cette fiche</span>
              </dd>
            </div>
            <div>
              <dt>Responsable KATANA</dt>
              <dd>Affectation indisponible</dd>
            </div>
          </dl>
        </section>
      </div>
      <div className="client-activity__support">
        <section className="client-directory__table-card">
          <header>
            <div>
              <p className="client-directory__eyebrow">Support</p>
              <h2>Derniers tickets SAV</h2>
            </div>
            <button
              type="button"
              className="client-activity__sav"
              disabled
              aria-describedby={availabilityId}
            >
              Ouvrir le SAV
            </button>
          </header>
          <p>Les tickets de ce Client seront disponibles avec le module SAV.</p>
        </section>
        <section className="client-directory__table-card">
          <header>
            <div>
              <p className="client-directory__eyebrow">Documentation</p>
              <h2>FAQ disponibles</h2>
            </div>
            <button
              type="button"
              className="client-activity__faq"
              disabled
              aria-describedby={availabilityId}
            >
              Voir la FAQ
            </button>
          </header>
          <p>Les tutoriels de ce Client seront disponibles avec le module FAQ.</p>
        </section>
      </div>
    </section>
  );
}
